import type { Dayjs } from 'dayjs';
import type React from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { TableComponents } from 'react-virtuoso';
import { TableVirtuoso } from 'react-virtuoso';
import styled from '@emotion/styled';
import { Skeleton, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { SkeletonRows, TableHeaderCell } from '@components/table';
import { EmptyRow } from '@components/table/EmptyRow';
import { useStore } from '@stores/RootStore';
import type { ProductSubscriptionListModel } from '@repositories/productSubscriptionRepository/models/ProductSubscriptionListModel';
import type {
  ProductSubscriptionFilter,
  ProductSubscriptionSortString,
} from '@repositories/productSubscriptionRepository/productSubscription.types';
import { useDeleteSubscription, useSubscriptionsBySoftwareIds } from '@queryHooks/useSubscription';
import { getSortOrder } from '@utils/CommonUtil';
import { dateFormat } from '@utils/dateUtil';
import type { SubscriptionListModel } from '@models/SubscriptionListModel';
import { SubscriptionEditDialog } from '../../subscriptionEditDialog/SubscriptionEditDialog';
import { SubscriptionGroupHeader } from './SubscriptionGroupHeader';
import { SubscriptionRow } from './SubscriptionRow';

type GroupedTableItem =
  | { type: 'group'; product: ProductSubscriptionListModel }
  | { type: 'subscription'; subscription: SubscriptionListModel; softwareId: string }
  | { type: 'subscription-skeleton'; softwareId: string; index: number };

/** 빈 결과/초기 로딩 시 노출되는 스켈레톤 행 수. */
const EMPTY_SKELETON_ROW_COUNT = 3;
/** 그룹 펼침 시 fallback 스켈레톤 행 수 (totalSubscriptionCount 미상). */
const GROUP_FALLBACK_SKELETON_ROW_COUNT = 3;
/** 그룹 펼침 시 노출 가능한 최대 스켈레톤 행 수 (UI 보호 한계). */
const GROUP_MAX_SKELETON_ROW_COUNT = 20;
/** 테이블 컬럼 수 (헤더와 일치 필수). */
const TABLE_COLUMN_COUNT = 8;

type TableContext = {
  isLoading: boolean;
  emptyDataMessage: string;
  onRowClick: (item: GroupedTableItem) => void;
};

const GroupHeaderRow = styled(TableRow)`
  cursor: pointer;
  background-color: ${({ theme }) => theme.colors['bg-white']};

  &:hover {
    background-color: ${({ theme }) => theme.colors['state-white-hover']};
  }
`;

const SubscriptionItemRow = styled(TableRow)`
  cursor: pointer;
  background-color: ${({ theme }) => theme.colors['bg-gray-light']};
  &:hover {
    background-color: ${({ theme }) => theme.colors['state-white-primary-hover']};
  }
`;

const SubscriptionSkeletonRow = styled(TableRow)`
  background-color: ${({ theme }) => theme.colors['bg-gray-light']};
`;

const StyledTable = styled(Table)`
  border-radius: 5px;
  box-shadow: 0 0 0 1px ${({ theme }) => theme.colors['border-gray-lighter']};
`;

const tableComponents: TableComponents<GroupedTableItem, TableContext> = {
  Table: StyledTable,
  TableHead: TableHead as TableComponents<GroupedTableItem, TableContext>['TableHead'],
  TableRow: ({ children, item, context, ...props }) => {
    if (item?.type === 'group') {
      return <GroupHeaderRow {...props}>{children}</GroupHeaderRow>;
    }
    if (item?.type === 'subscription-skeleton') {
      return <SubscriptionSkeletonRow {...props}>{children}</SubscriptionSkeletonRow>;
    }
    return (
      <SubscriptionItemRow
        {...props}
        onClick={() => {
          if (typeof context?.onRowClick === 'function' && item) {
            context.onRowClick(item);
          }
        }}
      >
        {children}
      </SubscriptionItemRow>
    );
  },
  TableBody: TableBody as TableComponents<GroupedTableItem, TableContext>['TableBody'],
  EmptyPlaceholder: ({ context }) =>
    context ? (
      <TableBody>
        {context.isLoading ? (
          <SkeletonRows rowNum={EMPTY_SKELETON_ROW_COUNT} colNum={TABLE_COLUMN_COUNT} />
        ) : (
          <EmptyRow colSpan={TABLE_COLUMN_COUNT} title={context.emptyDataMessage} />
        )}
      </TableBody>
    ) : null,
};

type Props = {
  isLoading: boolean;
  filter: ProductSubscriptionFilter & { filterType?: string };
  productList: ProductSubscriptionListModel[];
  scrollTarget: HTMLElement | null;
  onUpdateFilter: (filter: Partial<ProductSubscriptionFilter>) => void;
  endReached: () => void;
};

export const SubscriptionTable: React.FC<Props> = ({
  isLoading,
  filter,
  productList,
  scrollTarget,
  onUpdateFilter,
  endReached,
}) => {
  const { t } = useTranslation();
  const {
    uiStore: { alertStore, toastStore },
  } = useStore();
  const navigate = useNavigate();

  const [subscriptionEditTarget, setSubscriptionEditTarget] = useState<SubscriptionListModel | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const expandedIds = useMemo(() => Array.from(expandedGroups), [expandedGroups]);

  const subscriptionQueries = useSubscriptionsBySoftwareIds(expandedIds, filter);

  const subscriptionLoadingBySoftwareId = useMemo(
    () => Object.fromEntries(expandedIds.map((id, idx) => [id, Boolean(subscriptionQueries[idx]?.isLoading)])),
    [expandedIds, subscriptionQueries],
  );

  const subscriptionsBySoftwareId = useMemo(
    () => Object.fromEntries(expandedIds.map((id, idx) => [id, subscriptionQueries[idx]?.data ?? []])),
    [expandedIds, subscriptionQueries],
  );

  const flattenedData = useMemo<GroupedTableItem[]>(() => {
    const result: GroupedTableItem[] = [];
    productList.forEach(product => {
      result.push({ type: 'group', product });
      if (!expandedGroups.has(product.softwareId)) return;

      if (subscriptionLoadingBySoftwareId[product.softwareId]) {
        // totalSubscriptionCount 기반 클램프, 미상이면 fallback 사용.
        const expected = product.totalSubscriptionCount;
        const skeletonCount = Math.min(
          GROUP_MAX_SKELETON_ROW_COUNT,
          Number.isFinite(expected) && expected > 0 ? expected : GROUP_FALLBACK_SKELETON_ROW_COUNT,
        );
        for (let i = 0; i < skeletonCount; i += 1) {
          result.push({ type: 'subscription-skeleton', softwareId: product.softwareId, index: i });
        }
        return;
      }

      (subscriptionsBySoftwareId[product.softwareId] ?? []).forEach(subscription => {
        result.push({ type: 'subscription', subscription, softwareId: product.softwareId });
      });
    });
    return result;
  }, [productList, expandedGroups, subscriptionsBySoftwareId, subscriptionLoadingBySoftwareId]);

  const handleToggleGroup = (softwareId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(softwareId)) {
        next.delete(softwareId);
      } else {
        next.add(softwareId);
      }
      return next;
    });
  };

  const handleRowClick = (item: GroupedTableItem) => {
    if (item.type === 'subscription') {
      navigate(item.subscription.id);
    }
  };

  const handleHeaderCellClick = (orderBy: string, order: 'asc' | 'desc' | undefined) => {
    setExpandedGroups(new Set());
    onUpdateFilter({
      sort: order ? [`${orderBy}.${order}` as ProductSubscriptionSortString] : [],
    });
  };

  const handleFilter = {
    checkFilterChange: (filterKey: string, filterValues: string[]) => {
      onUpdateFilter({ [filterKey]: filterValues });
    },
    dateFilterChange: (filterKey: string, from: Dayjs | null, to: Dayjs | null) => {
      const filterKeyFrom = dateFormat(from, undefined, 'YYYY-MM-DD');
      const filterKeyTo = dateFormat(to, undefined, 'YYYY-MM-DD');
      onUpdateFilter({
        [`${filterKey}From`]: filterKeyFrom,
        [`${filterKey}To`]: filterKeyTo,
      } as Partial<ProductSubscriptionFilter>);
    },
  };

  const { mutate: deleteSubscription } = useDeleteSubscription();

  const renderFixedHeaderContent = () => (
    <TableRow>
      <TableHeaderCell
        width={400}
        orderBy="softwareName"
        order={getSortOrder(filter.sort, 'softwareName')}
        onSortChange={handleHeaderCellClick}
        disableSort={productList.length === 0}
      >
        {t('Subscrib_Main_Column_ProductAndSubscription')}
      </TableHeaderCell>
      <TableHeaderCell width={180}>{t('Subscrib_Main_12')}</TableHeaderCell>
      <TableHeaderCell
        width={140}
        orderBy="annualPaymentEstimate"
        order={getSortOrder(filter.sort, 'annualPaymentEstimate')}
        onSortChange={handleHeaderCellClick}
        disableSort={productList.length === 0}
      >
        {t('Subscrib_Main_Column_AnnualCost')}
      </TableHeaderCell>
      <TableHeaderCell
        width={170}
        orderBy="nextExpiryDate"
        order={getSortOrder(filter.sort, 'nextExpiryDate')}
        onSortChange={handleHeaderCellClick}
        disableSort={productList.length === 0}
        filterType="date"
        filterKey="nextExpiryDate"
        onFilterChange={handleFilter.dateFilterChange}
      >
        {t('Subscrib_Main_Column_NextExpiry')}
      </TableHeaderCell>
      <TableHeaderCell
        width={160}
        filterType="check"
        filterKey="status"
        itemList={[
          { displayName: t('Subscrib_Main_02'), filterValue: 'ACTIVE' },
          { displayName: t('Subscrib_Main_05'), filterValue: 'INACTIVE' },
        ]}
        onFilterChange={handleFilter.checkFilterChange}
        disableSort
      >
        {t('Subscrib_Main_15')}
      </TableHeaderCell>
      <TableHeaderCell width={150} disableSort>
        {t('Subscrib_Main_17')}
      </TableHeaderCell>
      <TableHeaderCell width={200} disableSort>
        {t('Subscrib_Main_16')}
      </TableHeaderCell>
      <TableCell width={72} align="center">
        {t('Acc_Main_23')}
      </TableCell>
    </TableRow>
  );

  const handleMenuClick = (menuKey: 'EDIT' | 'CONNECTION_SETTING' | 'DELETE', target: SubscriptionListModel) => {
    switch (menuKey) {
      case 'EDIT':
        setSubscriptionEditTarget(target);
        break;
      case 'CONNECTION_SETTING':
        navigate(target.connection?.isConnectionExist ? `../connections/${target.id}/connection` : '../connections');
        break;
      case 'DELETE':
        if (target.isExternalSubscription) {
          if (target.connection?.isConnected) {
            alertStore.open({
              title: t('Subscrib_Main_26'),
              message: t('Subscrib_Delete_04'),
            });
          } else {
            alertStore.open({
              title: t('Subscrib_Main_24'),
              message: t('Subscrib_Delete_01'),
              type: 'error',
              confirmName: t('Subscrib_Detail_BuyRequest_12'),
              onConfirm: () => {
                deleteSubscription(target.id);
                toastStore.open({
                  message: t('Subscrib_Delete_05'),
                  hideAction: true,
                });
              },
              useCancel: true,
            });
          }
        }
        break;
    }
  };

  const renderItemContent = (_index: number, item: GroupedTableItem) => {
    if (item.type === 'group') {
      return (
        <SubscriptionGroupHeader
          key={item.product.softwareId}
          product={item.product}
          isExpanded={expandedGroups.has(item.product.softwareId)}
          onToggle={() => handleToggleGroup(item.product.softwareId)}
        />
      );
    }
    if (item.type === 'subscription-skeleton') {
      return (
        <>
          {Array.from({ length: TABLE_COLUMN_COUNT }).map((_, idx) => (
            <TableCell key={idx}>
              <Skeleton />
            </TableCell>
          ))}
        </>
      );
    }
    return <SubscriptionRow key={item.subscription.id} model={item.subscription} onMenuClick={handleMenuClick} />;
  };

  return (
    <>
      <TableVirtuoso
        data={flattenedData}
        components={tableComponents}
        fixedHeaderContent={renderFixedHeaderContent}
        itemContent={renderItemContent}
        endReached={endReached}
        context={{
          isLoading,
          emptyDataMessage: t('Subscrib_Main_Filter_08'),
          onRowClick: handleRowClick,
        }}
        increaseViewportBy={{ top: 840, bottom: 840 }}
        customScrollParent={scrollTarget ?? undefined}
      />

      <SubscriptionEditDialog
        subscriptionId={subscriptionEditTarget?.id ?? ''}
        softwareId={subscriptionEditTarget?.software.id ?? ''}
        open={!!subscriptionEditTarget}
        onClose={() => setSubscriptionEditTarget(null)}
      />
    </>
  );
};
